import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { client } from "@/lib/api";
import SEO from "@/components/SEO";
import ShareButtons from "@/components/ShareButtons";
import SaveToCaderno from "@/components/SaveToCaderno";
import RadarHubBackLink from "@/components/RadarHubBackLink";
import AccessibleReader from "@/components/AccessibleReader";
import { trackEvent } from "@/lib/ga4";
// Iter288 — Sanitização defensiva de texto de leis (mojibake, hifens,
// quebras de linha rígidas). Aplica no fallback de texto plano.
import { sanitizeLegalText } from "@/lib/legalTextSanitizer";
import {
  Library, ExternalLink, Loader2, ChevronLeft, Search, AlertTriangle,
  BookOpen, ChevronDown, Scale, Video, FileText, Link2, Printer,
} from "lucide-react";

/**
 * /noticias-legislativas/lb/:slug — Página nativa da Lei Básica.
 *
 * Renderiza o texto integral de uma norma (CPC, NLLC, Lei 9.784, etc.)
 * extraído do Planalto, estruturado em árvore (Artigo > Parágrafo >
 * Inciso > Alínea), com:
 *   - filtro por Art. NN (input no topo)
 *   - link ancorado por artigo (#art-NN) — útil para citação direta
 *   - link de volta ao Planalto (auditável)
 *   - share kit
 *   - aviso visível quando parse_quality != "ok"
 *
 * O cruzamento com decisões do STF (Fase 4) será injetado dentro de
 * cada <ArticleBlock /> num próximo job — placeholder já demarcado.
 */
export default function LegalActLBPage() {
  const { slug } = useParams();
  const [act, setAct] = useState(null);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [manualRefs, setManualRefs] = useState({});  // by_article: { "5": [ref, ref], "12": [...] }
  const [stfCounts, setStfCounts] = useState({});    // {"5": 3, "10": 0, ...}

  // Container do texto integral — alimentado ao AccessibleReader como
  // alvo do TTS (Web Speech API lê innerText do nó referenciado).
  const articlesRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setAct(null);
    setError(false);
    setManualRefs({});
    setStfCounts({});
    client.get(`/legal-acts/lb/${encodeURIComponent(slug)}`)
      .then((r) => { if (!cancelled) setAct(r.data); })
      .catch(() => { if (!cancelled) setError(true); });
    // Carrega refs manuais em paralelo — silencia 404 (LB sem refs ainda).
    client.get(`/legal-acts/lb/${encodeURIComponent(slug)}/refs`)
      .then((r) => { if (!cancelled) setManualRefs(r.data.by_article || {}); })
      .catch(() => { /* ignora */ });
    // Carrega contagens STF — usadas para esconder o toggle "Decisões do STF"
    // nos artigos sem nenhum hit (UX mais limpa em leis grandes).
    client.get(`/legal-acts/lb/${encodeURIComponent(slug)}/stf-counts`)
      .then((r) => { if (!cancelled) setStfCounts(r.data.counts || {}); })
      .catch(() => { /* ignora — toggle simplesmente não aparece */ });
    return () => { cancelled = true; };
  }, [slug]);

  const filteredArticles = useMemo(() => {
    if (!act?.articles) return [];
    if (!query.trim()) return act.articles;
    // Match por número (ex.: "5") ou por texto literal.
    const q = query.trim().toLowerCase();
    const isNumber = /^\d+$/.test(q);
    return act.articles.filter((a) => {
      if (isNumber && a.number === q) return true;
      const blob = (a.caput + " " + (a.paragraphs || []).map((p) => p.text).join(" ") + " " + (a.incisos || []).map((i) => i.text).join(" ")).toLowerCase();
      return blob.includes(q);
    });
  }, [act, query]);

  if (error) {
    return (
      <div className="min-h-screen pt-32 px-4 text-center" data-testid="lb-error">
        {/* Iter287 — noindex sinaliza ao Google Search Console para
            remover essa URL do índice (evita "Soft 404" com HTTP 200
            em rotas SPA de LB inexistente ou soft-deletada). */}
        <SEO
          title="Lei Básica não encontrada · JurisTube"
          url="/noticias-legislativas"
          noindex
        />
        <h1 className="font-serif text-2xl text-white">Lei Básica não encontrada</h1>
        <Link to="/noticias-legislativas" className="inline-block mt-4 text-violet-300 hover:underline">
          ← Voltar ao Radar Normativo
        </Link>
      </div>
    );
  }

  if (!act) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center text-slate-400" data-testid="lb-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando lei…
      </div>
    );
  }

  const fetchedFmt = act.fetched_at
    ? new Date(act.fetched_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })
    : "";

  return (
    <div className="min-h-screen pt-28 md:pt-32 pb-20 px-4 sm:px-6 lg:px-10 xl:px-14 2xl:px-20" data-testid="lb-page">
      <SEO
        title={`${act.title} — Texto integral · JurisTube`}
        description={`Leia o texto integral consolidado de ${act.title} (${act.articles_count} artigos), com fonte oficial do Planalto e ferramentas de busca por artigo.`}
        url={`/noticias-legislativas/lb/${act.slug}`}
        image="/brand/radar-normativo-1200x630.jpg"
      />
      <article className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-3" data-testid="lb-nav-row">
          <Link
            to="/noticias-legislativas"
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-violet-300"
            data-testid="lb-back-link"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Radar Normativo
          </Link>
          <RadarHubBackLink accent="violet" testid="lb-back-to-hub" />
        </div>

        {/* Cabeçalho da lei */}
        <header className="space-y-3" data-testid="lb-header">
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest text-white"
            style={{ background: act.color || "#ea580c" }}
            data-testid="lb-badge"
          >
            <Library className="w-3 h-3" />
            LB · Lei Básica
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-white leading-tight">
            {act.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
            {act.number && <span data-testid="lb-meta-number">Lei nº {act.number}{act.year ? `/${act.year}` : ""}</span>}
            <span data-testid="lb-meta-count">
              <BookOpen className="inline w-3 h-3 mr-1" />
              {act.articles_count} artigo(s) indexado(s)
            </span>
            <span data-testid="lb-meta-fetched">Sincronizado com Planalto em {fetchedFmt}</span>
          </div>
          {act.source_url && (
            <a
              href={act.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-violet-300 hover:underline"
              data-testid="lb-source-link"
            >
              <ExternalLink className="w-3 h-3" /> Texto-fonte oficial no Planalto
            </a>
          )}
        </header>

        {/* ─── Trilha de utilidade & compartilhamento ────────────────
            Padroniza com as páginas doutrinárias: share buttons +
            imprimir/PDF + leitor por voz (Web Speech API). O TTS
            consome `articlesRef` (container dos artigos abaixo). */}
        <div
          className="flex flex-wrap items-center gap-2 print:hidden"
          data-testid="lb-utility-bar"
        >
          <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider mr-1">
            Compartilhar:
          </span>
          {/* ShareButtons aponta para o endpoint OG ``/api/og/legal-acts/{slug}``
              para que WhatsApp/Telegram/LinkedIn leiam og:title, og:description e
              og:image individualizados desta LB (padrão idêntico ao de notícias,
              decisões temáticas e informativos). O endpoint redireciona o usuário
              humano para a página canônica. */}
          <ShareButtons
            url={
              typeof window !== "undefined"
                ? `${window.location.origin}/api/og/legal-acts/${act.slug}`
                : `https://juristube.com.br/api/og/legal-acts/${act.slug}`
            }
            text={`${act.title} — texto integral, com cruzamento de jurisprudência do STF, no JurisTube.`}
            testid="lb-share"
          />
          <SaveToCaderno
            variant="pill"
            target={{
              type: "artigo",
              id: `lb-${act.id}`,
              meta: {
                title: act.title,
                slug: act.slug,
                author_slug: "normas-basicas",
              },
            }}
          />
          <button
            type="button"
            onClick={() => {
              if (typeof window !== "undefined") {
                trackEvent("legal_act_print", {
                  act_slug: act.slug, act_type: "LB", act_number: act.number,
                });
                window.print();
              }
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-semibold transition"
            data-testid="lb-print"
            title="Abre a janela de impressão com cabeçalho JurisTube. Escolha 'Salvar como PDF' para gerar uma cópia para citação."
          >
            <Printer className="w-3.5 h-3.5" /> Imprimir / salvar PDF
          </button>
          <AccessibleReader targetRef={articlesRef} title={act.title} />
        </div>

        {/* Aviso de parse parcial */}
        {act.parse_quality !== "ok" && (
          <div
            className="rounded-xl border border-amber-500/40 bg-amber-500/[0.08] p-3 flex items-start gap-2 text-xs text-amber-100"
            data-testid="lb-parse-warning"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <strong className="font-semibold">Parsing parcial — revisão recomendada.</strong>
              {(act.parse_warnings || []).map((w, i) => (
                <p key={i} className="text-amber-100/80">{w}</p>
              ))}
              <p className="text-amber-100/70">
                Para fidelidade absoluta, confira sempre com o{" "}
                <a href={act.source_url} target="_blank" rel="noopener noreferrer" className="underline">texto oficial</a>.
              </p>
            </div>
          </div>
        )}

        {/* Busca / filtro por artigo */}
        {act.parse_quality !== "fallback_plain" && act.articles?.length > 0 && (
          <div className="relative" data-testid="lb-search-wrap">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Buscar por número (ex.: "5") ou por palavra…'
              className="w-full bg-slate-900/60 border border-slate-800 rounded-lg pl-10 pr-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-violet-400/40"
              data-testid="lb-search-input"
            />
            {query && (
              <div className="text-[11px] text-slate-500 mt-1" data-testid="lb-search-count">
                {filteredArticles.length} artigo(s) correspondente(s)
              </div>
            )}
          </div>
        )}

        {/* Corpo: árvore hierárquica OU texto plano (fallback) */}
        {act.parse_quality === "fallback_plain" || (act.articles?.length || 0) === 0 ? (
          <div
            className="whitespace-pre-wrap text-sm text-slate-300 leading-relaxed bg-slate-900/40 rounded-lg p-4"
            data-testid="lb-plain-text"
          >
            {/* Iter288 — sanitiza texto plano antes de renderizar como
                parágrafos. Cada linha vira <p> ao invés de bloco único
                (elimina espaçamento confuso e melhora leitura). */}
            {sanitizeLegalText(act.full_text || "")
              .split("\n")
              .map((line, i) => {
                const t = line.trim();
                if (!t) return <div key={i} className="h-2" />;
                return (
                  <p key={i} className="mb-1.5">
                    {t}
                  </p>
                );
              })}
          </div>
        ) : (
          <div ref={articlesRef} className="space-y-4" data-testid="lb-articles">
            {filteredArticles.map((a) => (
              <ArticleBlock
                key={a.anchor}
                article={a}
                actSlug={act.slug}
                manualRefs={manualRefs[a.number] || []}
                stfCount={stfCounts[a.number] || 0}
              />
            ))}
            {filteredArticles.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-800 p-6 text-center text-slate-500 text-sm" data-testid="lb-no-results">
                Nenhum artigo corresponde à busca.
              </div>
            )}
          </div>
        )}

        {/* Footer info */}
        <footer className="text-[10px] text-slate-600 text-center pt-6 border-t border-slate-800/50">
          Coletado e estruturado automaticamente do Planalto · A fonte oficial sempre prevalece em caso de divergência ·
          Atualizado em {fetchedFmt}
        </footer>
      </article>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ArticleBlock — bloco individual de cada Art. com caput + § + I/II/III + a)b)
// Inclui âncora HTML (#art-NN) para citação direta.
// ─────────────────────────────────────────────────────────────────────────
function ArticleBlock({ article, actSlug, manualRefs = [], stfCount = 0 }) {
  const revogado = article.status === "revogado";
  const [stfOpen, setStfOpen] = useState(false);
  const [stfData, setStfData] = useState(null);
  const [stfLoading, setStfLoading] = useState(false);
  const [stfError, setStfError] = useState(false);

  async function ensureStfLoaded() {
    if (stfData !== null || stfLoading) return;
    setStfLoading(true);
    setStfError(false);
    try {
      const r = await client.get(
        `/legal-acts/lb/${actSlug}/article/${article.number}/stf-refs`,
      );
      setStfData(r.data);
    } catch {
      setStfError(true);
      setStfData({ refs: [], count: 0 });
    } finally {
      setStfLoading(false);
    }
  }

  function toggleStf() {
    const next = !stfOpen;
    setStfOpen(next);
    if (next) ensureStfLoaded();
  }

  return (
    <section
      id={article.anchor}
      className={`rounded-xl border ${revogado ? "border-red-500/20 bg-red-500/[0.03]" : "border-slate-800 bg-slate-900/30"} p-4 sm:p-5 space-y-2 scroll-mt-24`}
      data-testid={`lb-article-${article.number}`}
    >
      <div className="flex items-baseline gap-2 flex-wrap">
        <a
          href={`#${article.anchor}`}
          className="font-mono text-xs text-violet-300 hover:underline shrink-0"
          data-testid={`lb-article-${article.number}-anchor`}
        >
          {article.label}
        </a>
        {revogado && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 border border-red-500/40 text-red-200 uppercase tracking-widest font-semibold">
            Revogado
          </span>
        )}
      </div>
      {article.caput && (
        <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">
          {article.caput}
        </p>
      )}

      {(article.incisos || []).length > 0 && (
        <ul className="pl-5 space-y-1 text-sm text-slate-300 leading-relaxed">
          {article.incisos.map((inc, i) => (
            <li key={`inc-${i}`}>
              <span className="font-mono text-violet-300/80 mr-1.5">{inc.number} —</span>
              {inc.text}
              {(inc.alineas || []).length > 0 && (
                <ul className="pl-5 mt-1 space-y-0.5 text-xs">
                  {inc.alineas.map((al, j) => (
                    <li key={`al-${j}`}>
                      <span className="font-mono text-slate-500 mr-1">{al.number})</span>
                      {al.text}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}

      {(article.paragraphs || []).map((p, i) => (
        <div key={`par-${i}`} className="pl-3 border-l-2 border-slate-800 ml-1 space-y-1">
          <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
            <span className="font-mono text-violet-300/80 mr-1.5">{p.label}</span>
            {p.text}
          </p>
          {(p.incisos || []).length > 0 && (
            <ul className="pl-5 space-y-0.5 text-xs text-slate-400">
              {p.incisos.map((inc, j) => (
                <li key={`pinc-${j}`}>
                  <span className="font-mono text-slate-500 mr-1">{inc.number} —</span>
                  {inc.text}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}

      {/* ─── Referências manuais (editoria): vídeos/artigos do acervo ─── */}
      {manualRefs.length > 0 && (
        <div
          className="pt-3 mt-2 border-t border-slate-800/50 space-y-1.5"
          data-testid={`lb-article-${article.number}-manual-refs`}
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-orange-300/90 font-semibold flex items-center gap-1.5">
            <Link2 className="w-3 h-3" />
            No acervo JurisTube
          </div>
          {manualRefs.map((ref) => (
            <a
              key={ref.id}
              href={ref.target_url}
              className="block rounded-lg border border-orange-500/20 bg-orange-500/[0.05] hover:bg-orange-500/[0.09] hover:border-orange-500/40 p-2.5 transition group"
              data-testid={`lb-article-${article.number}-manual-ref-${ref.id}`}
            >
              <div className="flex items-start gap-2">
                {ref.target_type === "episode" ? (
                  <Video className="w-3.5 h-3.5 text-orange-300 shrink-0 mt-0.5" />
                ) : (
                  <FileText className="w-3.5 h-3.5 text-orange-300 shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-100 font-semibold leading-snug group-hover:text-orange-200">
                    {ref.target_title}
                  </div>
                  {(ref.target_authors || []).length > 0 && (
                    <div className="text-[10px] text-slate-500 truncate mt-0.5">
                      {ref.target_authors.join(", ")}
                      {ref.target_podcast ? ` · ${ref.target_podcast}` : ""}
                      {ref.target_source ? ` · ${ref.target_source}` : ""}
                    </div>
                  )}
                  {ref.note && (
                    <p className="text-[11px] text-slate-400 italic mt-1 leading-snug">
                      &ldquo;{ref.note}&rdquo;
                    </p>
                  )}
                </div>
              </div>
            </a>
          ))}
        </div>
      )}

      {/* ─── Cruzamento Fase 4: Decisões STF que citam este artigo ───
          Só renderiza quando o pré-load /stf-counts indicou ≥1 hit.
          Evita poluir leis grandes (ex.: CPC com 1084 artigos onde
          apenas alguns têm citação no STF). */}
      {stfCount > 0 && (
      <div className="pt-2 mt-2 border-t border-slate-800/50">
        <button
          type="button"
          onClick={toggleStf}
          className="inline-flex items-center gap-1.5 text-[11px] text-violet-300/80 hover:text-violet-200 transition"
          data-testid={`lb-article-${article.number}-stf-toggle`}
        >
          <Scale className="w-3 h-3" />
          <span>Decisões do STF citando este artigo</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${stfOpen ? "rotate-180" : ""}`} />
          <span className="ml-1 px-1.5 py-0.5 rounded-full bg-violet-500/15 border border-violet-500/30 text-[9px] font-bold">
            {stfData?.count ?? stfCount}
          </span>
        </button>
        {stfOpen && (
          <div className="mt-2 space-y-2" data-testid={`lb-article-${article.number}-stf-panel`}>
            {stfLoading && (
              <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Buscando decisões…
              </div>
            )}
            {!stfLoading && stfError && (
              <div className="text-[11px] text-red-300">Falha ao carregar decisões.</div>
            )}
            {!stfLoading && stfData && stfData.refs.length === 0 && (
              <div className="text-[11px] text-slate-500 italic">
                Nenhuma decisão do STF citando explicitamente este artigo foi encontrada no acervo do JurisTube.
              </div>
            )}
            {!stfLoading && stfData?.refs.map((ref, i) => (
              <div
                key={i}
                className="rounded-lg border border-violet-500/20 bg-violet-500/[0.04] p-2.5 space-y-1"
                data-testid={`lb-article-${article.number}-stf-ref-${i}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-violet-300 font-semibold">
                    {ref.source === "tematico" ? "Tema STF" : ref.edicao || "Informativo STF"}
                  </span>
                  {ref.processo && (
                    <span className="font-mono text-[10px] text-slate-400">{ref.processo}</span>
                  )}
                </div>
                <div className="text-xs text-slate-200 font-semibold leading-snug">
                  {ref.title}
                </div>
                <p className="text-[11px] text-slate-300 leading-relaxed">
                  {ref.snippet}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </section>
  );
}
